import { TextField, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { ReactComponent as ClearIcon } from '../../../../assets/icons/close-grey.svg';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';

type Props = {
  value: number | string;
  onChange: (value: number) => void;
};

export const LightNumericEditableCell = ({ value, onChange }: Props) => {
  const [showClear, setShowClear] = useState(false);
  const [inputValue, setInputValue] = useState(typeof value === 'number' ? `${value}%` : `0%`);

  // Debounce the input value to reduce onChange calls
  const debouncedInputValue = useDebouncedValue(inputValue, 500);

  // Sync with external value prop
  useEffect(() => {
    setInputValue(typeof value === 'number' ? `${value}%` : `0%`);
  }, [value]);

  // Handle debounced onChange
  useEffect(() => {
    // Don't trigger onChange on initial mount or when syncing with external value
    if (debouncedInputValue !== (typeof value === 'number' ? `${value}%` : `0%`)) {
      const numericValue = debouncedInputValue.match(/[0-9]+/g)?.join('') || '0';
      onChange(Number(numericValue));
    }
  }, [debouncedInputValue, onChange, value]);

  return (
    <NumberInputField
      autoComplete='off'
      name='numberformat'
      onFocus={(e) => {
        e.target.select();
      }}
      style={{
        width: '100%',
      }}
      className='formatted-numberformat-input'
      onMouseEnter={() => {
        if (value !== 0) {
          setShowClear(true);
        }
      }}
      onMouseLeave={() => {
        setShowClear(false);
      }}
      InputProps={{
        endAdornment: showClear ? (
          <ClearIcon
            style={{
              cursor: 'pointer',
              position: 'absolute',
              right: 5,
            }}
            onClick={() => {
              setInputValue('0%');
              onChange(0);
            }}
          />
        ) : null,
      }}
      size='small'
      type='string'
      value={inputValue}
      onChange={(e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setInputValue(`${value}%`);
      }}
      onBlur={() => {
        // On blur, immediately apply the change without waiting for debounce
        const numericValue = inputValue.match(/[0-9]+/g)?.join('') || '0';
        onChange(Number(numericValue));
      }}
    />
  );
};

const NumberInputField = styled(TextField)(({ theme }) => ({
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    display: 'none',
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: theme.colors.textAccent.disabled,
  },
  '& .MuiInputBase-root': {
    padding: 0,
    minWidth: 'unset' + ' !important',
  },
}));
